import { useRouter } from '@tanstack/react-router';
import type { MouseEvent, ReactNode } from 'react';
import { DiffRows } from './DiffRows';

/**
 * Small, safe markdown subset: fenced code, headings, lists, blockquotes, tables, rules, `code`, **bold**,
 * *italic* and links (markdown links and bare URLs: http, https, mailto, and /runs/<id> inside the app).
 * Agent output is untrusted — we only ever build React elements, never innerHTML.
 */
const INLINE = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\[[^\]\n]+\]\([^)\s]+\)|(?<![*\w])\*[^*\n]+\*(?![*\w]))/g;

/** App pages a chat message may point to; only these relative paths are ever linked. */
const INTERNAL_HREF = /^\/(?:runs|help\/a)\/[A-Za-z0-9_-]+$/;

/** The href to render for `url`, or null if it is unsafe to link (http, https, mailto, /runs/<id>, /help/a/<slug> only). */
export function safeHref(url: string): string | null {
  if (INTERNAL_HREF.test(url)) return url;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'mailto:' ? u.href : null;
  } catch {
    return null;
  }
}

/** Bare URLs and /runs/<id> references in plain text (the latter not as part of a longer path or URL). */
const BARE_LINK = /(?:https?:\/\/|mailto:)[^\s<>]+|(?<![\w/.:-])\/runs\/[A-Za-z0-9_-]+/g;

const count = (s: string, ch: string) => s.split(ch).length - 1;

/** Cuts trailing sentence punctuation and unbalanced closing brackets off a matched URL. */
function trimUrl(url: string): string {
  let end = url.length;
  while (end > 0) {
    const c = url[end - 1]!;
    const head = url.slice(0, end);
    if (/[.,;:!?'"]/.test(c)) end--;
    else if (c === ')' && count(head, ')') > count(head, '(')) end--;
    else if (c === ']' && count(head, ']') > count(head, '[')) end--;
    else break;
  }
  return url.slice(0, end);
}

/** A link in a chat message: external ones open in a new tab, /runs/<id> navigates inside the app. */
export function ChatLink({ href, children }: { href: string; children: ReactNode }) {
  const router = useRouter({ warn: false });
  if (INTERNAL_HREF.test(href)) {
    const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (!router || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
        return;
      e.preventDefault();
      router.history.push(href);
    };
    return (
      <a href={href} onClick={onClick} className="text-accent break-all hover:underline">
        {children}
      </a>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent break-all hover:underline"
    >
      {children}
    </a>
  );
}

/** Plain text with bare URLs turned into links; everything else stays text. */
export function linkify(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(BARE_LINK)) {
    const at = m.index ?? 0;
    const url = trimUrl(m[0]);
    const href = url ? safeHref(url) : null;
    if (!href) continue;
    if (at > last) out.push(text.slice(last, at));
    out.push(
      <ChatLink key={`${keyBase}-l${i++}`} href={href}>
        {url}
      </ChatLink>,
    );
    last = at + url.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** A user's message: shown as written (whitespace and all), with links made clickable. */
export function Linkified({ text }: { text: string }) {
  return <>{linkify(text, 'u')}</>;
}

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE)) {
    const tok = m[0];
    const at = m.index ?? 0;
    if (at > last) out.push(...linkify(text.slice(last, at), `${keyBase}-t${i}`));
    const key = `${keyBase}-${i++}`;
    if (tok.startsWith('`'))
      out.push(
        <code key={key} className="rounded-xs bg-panel-2 px-1 text-info">
          {tok.slice(1, -1)}
        </code>,
      );
    else if (tok.startsWith('**'))
      out.push(
        <strong key={key} className="font-semibold text-fg">
          {inline(tok.slice(2, -2), key)}
        </strong>,
      );
    else if (tok.startsWith('[')) {
      const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok)!;
      const href = safeHref(lm[2]!);
      out.push(
        href ? (
          <ChatLink key={key} href={href}>
            {lm[1]}
          </ChatLink>
        ) : (
          tok
        ),
      );
    } else
      out.push(
        <em key={key} className="italic">
          {linkify(tok.slice(1, -1), key)}
        </em>,
      );
    last = at + tok.length;
  }
  if (last < text.length) out.push(...linkify(text.slice(last), `${keyBase}-t${i}`));
  return out;
}

const LIST = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const cells = (line: string) =>
  line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());

function renderLines(lines: string[], keyBase: string): ReactNode[] {
  const blocks: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const key = `${keyBase}-${i}`;
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push(
        <div key={key} className="mt-2 mb-0.5 font-semibold text-fg first:mt-0">
          {inline(h[2]!, key)}
        </div>,
      );
      i++;
      continue;
    }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      blocks.push(<hr key={key} className="my-2 border-border" />);
      i++;
      continue;
    }
    if (/^\s*>/.test(line)) {
      const q: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i]!)) q.push(lines[i++]!.replace(/^\s*>\s?/, ''));
      blocks.push(
        <blockquote key={key} className="my-1 border-l-2 border-border-strong pl-3 text-muted">
          {renderLines(q, key)}
        </blockquote>,
      );
      continue;
    }
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1]!)) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i]!.includes('|') && lines[i]!.trim()) rows.push(cells(lines[i++]!));
      blocks.push(
        <div key={key} className="my-1.5 overflow-x-auto">
          <table className="min-w-full border-collapse text-left">
            <thead>
              <tr>
                {head.map((c, ci) => (
                  <th key={ci} className="border-b border-border px-2 py-1 font-semibold text-fg">
                    {inline(c, `${key}h${ci}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, ci) => (
                    <td key={ci} className="border-b border-border/60 px-2 py-1 align-top">
                      {inline(c, `${key}r${ri}c${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    const li = LIST.exec(line);
    if (li) {
      const ordered = /\d/.test(li[2]!);
      const baseIndent = li[1]!.length;
      const items: { text: string; sub: string[] }[] = [];
      while (i < lines.length) {
        const m = LIST.exec(lines[i]!);
        if (m && m[1]!.length <= baseIndent + 1) {
          items.push({ text: m[3]!, sub: [] });
          i++;
        } else if (m && items.length) {
          items[items.length - 1]!.sub.push(lines[i++]!);
        } else if (lines[i]!.trim() && /^\s{2,}\S/.test(lines[i]!) && items.length) {
          items[items.length - 1]!.text += ` ${lines[i++]!.trim()}`;
        } else break;
      }
      const Tag = ordered ? 'ol' : 'ul';
      blocks.push(
        <Tag
          key={key}
          className={`my-1 flex flex-col gap-0.5 pl-5 ${ordered ? 'list-decimal' : 'list-disc'} marker:text-faint`}
        >
          {items.map((it, ii) => (
            <li key={ii}>
              {inline(it.text, `${key}i${ii}`)}
              {it.sub.length ? renderLines(it.sub, `${key}s${ii}`) : null}
            </li>
          ))}
        </Tag>,
      );
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() &&
      !/^(#{1,6})\s/.test(lines[i]!) &&
      !LIST.test(lines[i]!) &&
      !/^\s*>/.test(lines[i]!)
    )
      para.push(lines[i++]!);
    blocks.push(
      <p key={key} className="my-1 first:mt-0 last:mb-0">
        {para.map((p, pi) => (
          <span key={pi}>
            {inline(p, `${key}p${pi}`)}
            {pi < para.length - 1 ? <br /> : null}
          </span>
        ))}
      </p>,
    );
  }
  return blocks;
}

export function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const parts = text.split(/```/);
  parts.forEach((part, idx) => {
    if (idx % 2 === 1) {
      const nl = part.indexOf('\n');
      const lang = nl > -1 ? part.slice(0, nl).trim() : '';
      const code = (nl > -1 ? part.slice(nl + 1) : part).replace(/\n$/, '');
      if (lang === 'diff') {
        blocks.push(<DiffRows key={`c${idx}`} text={code} />);
        return;
      }
      blocks.push(
        <pre
          key={`c${idx}`}
          className="my-1.5 overflow-x-auto rounded-md border border-border bg-bg p-2.5 font-mono text-[12.5px]"
        >
          {lang ? <div className="mb-1 text-2xs text-faint">{lang}</div> : null}
          <code>{code}</code>
        </pre>,
      );
      return;
    }
    blocks.push(...renderLines(part.split('\n'), `m${idx}`));
  });
  return <div className="md-body break-words">{blocks}</div>;
}
